<template>
  <dialog ref="dialogRef" class="search-task-dialog bg-transparent th-modal">
    <form method="dialog" class="th-modal-box max-w-[680px] p-0 pb-5 overflow-hidden">
      <!-- Header: Search Input -->
      <div class="sticky top-0 bg-white z-10 px-6 pt-6 pb-5">
        <div class="flex items-center gap-3">
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="t('SearchTaskDialog.searchPlaceholder')"
            class="th-input th-input-md flex-1 border-none outline-none pl-4"
            @input="handleSearch"
          />
          <button
            type="button"
            class="th-btn th-btn-plain th-btn-sm th-btn-only-icon"
            @click="closeDialog"
          >
            <PtIcon icon="SolidCloseBig" class="text-base fill-gray-400" />
          </button>
        </div>
      </div>

      <!-- New Tasks Button -->
      <div class="px-6">
        <button
          type="button"
          class="w-full flex items-center gap-2 px-4 py-3 !bg-gray-100 hover:bg-gray-100 rounded-lg transition-colors"
          @click="handleNewTask"
        >
          <PtIcon :icon="editIcon" class="text-lg fill-gray-600 text-gray-600" />
          <span class="text-sm font-medium text-gray-450">{{
            t('SearchTaskDialog.newTasks')
          }}</span>
        </button>
      </div>

      <!-- Task List -->
      <div class="pb-5 overflow-hidden">
        <div class="px-6 py-4 max-h-[500px] overflow-y-auto">
          <div v-if="isTaskLoading && filteredTasks.length === 0" class="text-center mt-6">
            <span class="th-loading th-loading-spinner th-loading-sm"></span>
          </div>
          <template v-else-if="filteredTasks.length > 0">
            <div v-for="group in groupedTasks" :key="group.date" class="mb-6 last:mb-0">
              <!-- Date Header -->
              <div class="text-xs text-gray-90 pl-[16px] mb-[10px]">
                {{ group.date }}
              </div>

              <!-- Task Items -->
              <div class="space-y-2">
                <div
                  v-for="task in group.tasks"
                  :key="task.id"
                  class="task-item py-[10px] px-[16px] rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  @click="handleTaskClick(task)"
                >
                  <div class="flex items-start gap-1">
                    <component
                      :is="getTaskIcon(task.source)"
                      class="patsnap-biz-icon shrink-0 text-base fill-gray-450"
                    />
                    <div class="flex gap-1 flex-1 min-w-0">
                      <div v-if="task.business_name" class="text-sm font-medium text-gray-450 mb-1 whitespace-nowrap mr-1">
                        {{ task.business_name }}:
                      </div>
                      <div class="text-sm text-gray-1100 line-clamp-1" :title="task.title">
                        {{ task.title }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <div v-else class="text-center py-12">
            <PtIcon icon="SolidSearchLeft" class="text-4xl fill-gray-300 mx-auto mb-3" />
            <p class="text-sm text-gray-400">{{ t('SearchTaskDialog.noTasksFound') }}</p>
          </div>
        </div>
      </div>
    </form>
  </dialog>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { PtIcon } from 'patsnap-biz';
import { useI18n } from 'vue-i18n';
import { cn, tw, en, jp } from '../infrastructure/locales';
import editIcon from '../assets/navbar-edit.svg';
import { IMenuType, type HistoryRowData, type NAV_MENU_TYPE } from '../types';
import AgentIcon from './icons/AgentIcon.vue';
import ResearchIcon from './icons/ResearchIcon.vue';
import PatentDisclosure from './icons/PatentDisclosure.vue';
import RiskIcon from './icons/RiskIcon.vue';
import AIFTOSearchIcon from './icons/AIFTOSearchIcon.vue';
import AINoveltySearchIcon from './icons/AINoveltySearchIcon.vue';
import AIPatentDisclosureIcon from './icons/AIPatentDisclosureIcon.vue';
import AISpecificationIcon from './icons/AISpecificationIcon.vue';
import AITranslationIcon from './icons/AITranslationIcon.vue';
import TrizMindIcon from './icons/TrizMindIcon.vue';
import CompanyScoutIcon from './icons/CompanyScoutIcon.vue';
import RDTechMindIcon from './icons/TechMindIcon.vue';
import LSChatIcon from './icons/LSChatIcon.vue';
import PLGIcon from './icons/PLGIcon.vue';

const { t } = useI18n({
  messages: {
    cn,
    en,
    jp,
    tw,
  },
});

interface Props {
  isTaskLoading: boolean;
  tasks?: HistoryRowData[];
}

const props = withDefaults(defineProps<Props>(), {
  isTaskLoading: false,
  tasks: () => [],
});

const emit = defineEmits<{
  (e: 'task-click', task: HistoryRowData): void;
  (e: 'new-task'): void;
  (e: 'close'): void;
  (e: 'search', query: string): void;
}>();

const dialogRef = ref<HTMLDialogElement | null>(null);
const searchQuery = ref('');

const filteredTasks = computed(() => {
  if (!searchQuery.value.trim()) {
    return props.tasks;
  }
  const query = searchQuery.value.toLowerCase();
  return props.tasks.filter((task) => task.title?.toLowerCase().includes(query));
});

const groupedTasks = computed(() => {
  const groups: { date: string; tasks: HistoryRowData[] }[] = [];
  const tasksByDate = new Map<string, HistoryRowData[]>();

  filteredTasks.value.forEach((task) => {
    // 使用国际化的日期标签
    // const date = t('SearchTaskDialog.today'); // 简化处理，实际应该根据创建时间分组
    if (!tasksByDate.has(task.created_at)) {
      tasksByDate.set(task.created_at, [task]);
    } else {
      tasksByDate.get(task.created_at)!.push(task);
    }
  });

  tasksByDate.forEach((tasks, date) => {
    groups.push({ date, tasks });
  });

  return groups;
});

function getTaskIcon(source: IMenuType | NAV_MENU_TYPE): string {
  // 根据任务类型返回对应图标
  const iconMap = {
    [IMenuType.PLG_QA]: PLGIcon,
    [IMenuType.LS_CHAT]: LSChatIcon,
    [IMenuType.TECH_RESEARCH]: ResearchIcon,
    [IMenuType.TECH_FMEA]: RiskIcon,
    [IMenuType.PATENT_DISCLOSURE]: PatentDisclosure,
    [IMenuType.AI_TRANSLATION]: AITranslationIcon,
    [IMenuType.AI_SPECIFICATION]: AISpecificationIcon,
    [IMenuType.AI_PATENT_DISCLOSURE]: AIPatentDisclosureIcon,
    [IMenuType.AI_NOVELTY_SEARCH]: AINoveltySearchIcon,
    [IMenuType.AI_FTO_SEARCH]: AIFTOSearchIcon,
    [IMenuType.AI_NEW_FTO_SEARCH]: AIFTOSearchIcon,
    [IMenuType.TRIZ_MIND]: TrizMindIcon,
    [IMenuType.COMPANY_SCOUT]: CompanyScoutIcon,
    [IMenuType.RD_TECH_MIND]: RDTechMindIcon,
  };
  // @ts-ignore
  return iconMap[source] || AgentIcon;
}

function closeDialog() {
  dialogRef.value?.close();
  emit('close');
}

function openDialog() {
  dialogRef.value?.showModal();
}

function handleSearch() {
  // 搜索逻辑已通过 computed 实现
  emit('search', searchQuery.value.trim());
}

function handleTaskClick(task: HistoryRowData) {
  emit('task-click', task);
  closeDialog();
}

function handleNewTask() {
  emit('new-task');
  closeDialog();
}

defineExpose({
  openDialog,
  closeDialog,
});
</script>

<style lang="scss">
.search-task-dialog {
  z-index: 900 !important;

  &::backdrop {
    z-index: 899 !important;
    background-color: rgba(0, 0, 0, 0.5);
  }

  .th-modal-box {
    @media (max-width: 1024px) {
      margin-left: 0;
    }
  }
}

.task-item {
  &:hover {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }
}

.line-clamp-2 {
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
